import React, { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import TextField from '@mui/material/TextField';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import MenuIcon from '@mui/icons-material/Menu';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { map } from 'lodash';
import { styled } from '@mui/system';
import { useTranslation } from 'react-i18next';

import OecdLogo from 'assets/oecd-logo.png';
import OecdLogoBig from 'assets/oecd-logo-big.png';

import { PRIVILEGES, useAuth } from './auth.ctx';


const MENU_LINKS = [
  { to: ``, label: 'Home' },
  { to: `about`, label: 'About' },
  { to: `instruments?mode=normal`, label: 'Full list' },
  { to: `instruments?mode=advanced`, label: 'Advanced search' },
  { to: `adherences`, label: 'Adherences' },
  { to: `stats`, label: 'Key figures' },
];

const StyledBox = styled(Box)(({ theme }) => ({
  height: '100%',
  color: 'white',
  textAlign: 'center',
  textDecoration: 'none',
  padding: '14px 10px 0 10px',
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: theme.palette.secondary.light
  }

}));


const StyledGrid = styled(Grid)(({ theme }) => ({
  height: '100%',
  color: 'white',
  textAlign: 'center',
  textDecoration: 'none',
  padding: '14px 10px 0 10px',
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: theme.palette.secondary.light
  }

}));

const checkLocation = (link, location) => {
  let pathName = location.pathname.split('/')[2];
  if (link === '' && pathName === undefined) {
    return true
  }

  if (link.includes('instruments')) {
    if (link === 'instruments?mode=normal' && location.search.includes('mode=normal')) {
      return true
    } else if (link === 'instruments?mode=advanced' && location.search.includes('mode=advanced')) {
      return true
    } else if (link === 'instruments?mode=publish' && location.search.includes('mode=publish')) {
      return true
    }
    return false;
  }

  return link.split('?')[0] === location.pathname.split('/')[2];
}

const changeLanguage = (nextLanguage) => {
  let path = window.location.pathname.split("/");
  path[1] = nextLanguage;
  window.location.replace(window.location.origin + path.join("/") + window.location.search)
}

const DesktopToolbarView = () => {
  const { lang } = useParams();
  const { t } = useTranslation();
  const auth = useAuth();
  const [openSearch, setOpenSearch] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Toolbar variant="dense" style={{ padding: 0 }}>
      <Grid container alignItems="center" style={{ height: 48 }}>
        {map(MENU_LINKS, link => {
          return (
            <StyledGrid
              item
              key={link.to === '' ? 'homepage' : link.to}
              component={NavLink}
              to={link.to}
              alignItems="center"
              style={{ backgroundColor: checkLocation(link.to, location) && '#037BC1' }}
            >
              <span style={{ fontFamily: `"Open Sans", sans-serif`, fontSize: '14px' }}>
                {t(link.label)}
              </span>
            </StyledGrid>
          )
        })}
        <Grid item xs={true} display="flex" justifyContent='flex-end' style={{ height: 48 }}>
          <Stack direction={'row'}>
            {auth.hasPrivilege(PRIVILEGES.editMetaLists) && (
              <StyledBox
                component={NavLink}
                to='instruments?mode=publish'
                style={{ backgroundColor: checkLocation('instruments?mode=publish', location) && '#037BC1' }}
              >
                <span style={{ marginTop: 20 }}>
                  {t('createpublish')}
                </span>
              </StyledBox>
            )}
            {auth.hasPrivilege(PRIVILEGES.editMetaLists) && (
              <StyledBox
                component={NavLink}
                to='settings'
                style={{ backgroundColor: checkLocation('settings', location) && '#037BC1' }}
              >
                <span style={{ marginTop: 20 }}>
                  {t('admin')}
                </span>
              </StyledBox>
            )}
            <StyledBox onClick={() => changeLanguage(lang === "en" ? "fr" : "en")}>
              <span style={{ marginTop: 20 }}>
                <ArrowRightIcon style={{ fontSize: 16 }}/>
                {lang === "en" ? "FR" : "EN"}
              </span>
            </StyledBox>
            <Box
              style={{ backgroundColor: '#e7eaec', color: '#004A78FF' }}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {openSearch && (
                <Stack direction={'row'} alignItems="center" justifyContent="center">
                  <TextField
                    label={t('Search') + "..."}
                    variant="standard"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        navigate(`/${lang}/instruments?mode=advanced&term=${e.target.value}`);
                        setOpenSearch(false);
                      }
                    }}
                  />
                  <ClearIcon
                    style={{ cursor: 'pointer', marginLeft: 10, marginRight: 10 }}
                    onClick={() => setOpenSearch(false)}
                  />
                </Stack>
              )}
              {!openSearch && (
                <SearchIcon
                  style={{ cursor: 'pointer', marginLeft: 10, marginRight: 10 }}
                  onClick={() => setOpenSearch(true)}
                />
              )}
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Toolbar>
  )
};

const MobileToolbarView = () => {
  const [open, setOpen] = React.useState(false);
  const { lang } = useParams();
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  return (
    <Toolbar variant="dense" style={{ padding: 0 }}>
      <Grid container alignItems="center">
        <Grid item sx={{ flexGrow: 1 }}>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            sx={{ mr: 2, ml: 2 }}
            onClick={toggleDrawer(true)}
          >
            <MenuIcon/>
          </IconButton>
        </Grid>
        <Grid item>
          <Button
            variant="text"
            onClick={() => changeLanguage(lang === "en" ? "fr" : "en")}
            sx={{ color: '#fff' }}
          >
            <ArrowRightIcon style={{ fontSize: 16 }}/>
            {lang === "en" ? "FR" : "EN"}
          </Button>
        </Grid>
      </Grid>
      <Drawer open={open} onClose={toggleDrawer(false)}>
        <Box sx={{ width: '300px' }} onClick={toggleDrawer(false)}>
          <List>
            {map(MENU_LINKS, link => {
              return (
                <ListItemButton
                  key={link.to === '' ? 'homepage' : link.to}
                  component={NavLink}
                  to={link.to}
                  selected={checkLocation(link.to, location)}
                >
                  <ListItemText>
                    {t(link.label)}
                  </ListItemText>
                </ListItemButton>
              )
            })}
            {auth.hasPrivilege(PRIVILEGES.editMetaLists) && (
              <ListItemButton
                component={NavLink}
                to='instruments?mode=publish'
                selected={checkLocation('instruments?mode=publish', location)}
              >
                <ListItemText>
                  {t('createpublish')}
                </ListItemText>
              </ListItemButton>
            )}
            {auth.hasPrivilege(PRIVILEGES.editMetaLists) && (
              <ListItemButton
                component={NavLink}
                to='settings'
                selected={checkLocation('settings', location)}
              >
                <ListItemText>
                  {t('admin')}
                </ListItemText>
              </ListItemButton>
            )}
          </List>
        </Box>
        <TextField
          fullWidth
          label={t('Search') + "..."}
          variant="filled"
          type="search"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.target.value) {
              e.preventDefault();
              setOpen(false);
              navigate(`/${lang}/instruments?mode=advanced&term=${e.target.value}`);
            }
          }}
        />
      </Drawer>
    </Toolbar>
  )
};

const MainMenu = () => {
  const { t } = useTranslation();
  const auth = useAuth();

  const isMobileView = useMediaQuery('(max-width:1024px)');
  const isDesktopView = !isMobileView;

  return (
    <Grid container>
      <Grid item xs={12} className={'showInPrint'}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <img src={OecdLogoBig} style={{ height: '100px' }}/>
          <Typography fontSize={20} fontWeight={'bold'} style={{ position: 'absolute', top: '30px', right: '30px' }}>
            {t('OECD Legal Instruments')}
          </Typography>
        </Stack>
      </Grid>
      <Grid item xs={12} sx={{ mt: 2, mb: 1 }} className={'hidePrint'}>
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          <Grid item style={{ cursor: 'pointer' }} component={NavLink} to=''>
            <img src={OecdLogo} style={{ height: '50px' }}/>
          </Grid>
          <Grid
            item xs={true}
            sx={{
              display: 'inline-block',
              ml: 1,
              fontSize: 24,
              lineHeight: '32px',
              letterSpacing: '0.05rem',
              fontFamily: '"Open Sans", sans-serif;'
            }}
          >
            {t('OECD Legal Instruments')}
          </Grid>
          <Grid item>
            <Stack direction={'row'} alignItems={"center"}>
              <Avatar
                sx={{ bgcolor: "#6ba5e7" }}
              >
                {auth.currentUser?.name ? auth.currentUser?.name[0] : ''}
              </Avatar>
              &nbsp;{auth.currentUser?.name}
            </Stack>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} className={'hidePrint'}>
        <AppBar position="static" sx={{ bgcolor: '#004B78', boxShadow: 'none' }}>
          {isDesktopView && <DesktopToolbarView/>}
          {isMobileView && <MobileToolbarView/>}
        </AppBar>
      </Grid>
      <Grid item xs={12} className={'showInPrint'}>
        <Box style={{ margin: 5, padding: 15, border: '2px solid #004B78' }}>
          <div dangerouslySetInnerHTML={{ __html: t('printDisclaimer') }}/>
        </Box>
      </Grid>
    </Grid>

  );
};
export default MainMenu;
